import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaImages } from 'react-icons/fa';

export default function AdminBeforeAfter() {
  useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    category: '',
    sort_order: 0,
    is_active: true,
  });
  const [savingProject, setSavingProject] = useState(false);

  const [imageManagerProject, setImageManagerProject] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get('/before-after/all');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const openProjectModal = (item = null) => {
    if (item) {
      setEditingProject(item);
      setProjectForm({
        title: item.title || '',
        description: item.description || '',
        category: item.category || '',
        sort_order: item.sort_order || 0,
        is_active: item.is_active,
      });
    } else {
      setEditingProject(null);
      setProjectForm({ title: '', description: '', category: '', sort_order: 0, is_active: true });
    }
    setShowProjectModal(true);
  };

  const closeProjectModal = () => {
    setShowProjectModal(false);
    setEditingProject(null);
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    if (!projectForm.title.trim()) {
      toast.error('Başlık zorunlu');
      return;
    }
    setSavingProject(true);
    try {
      if (editingProject) {
        await api.put(`/before-after/${editingProject.id}`, projectForm);
        toast.success('Proje güncellendi');
      } else {
        const res = await api.post('/before-after', projectForm);
        toast.success('Proje oluşturuldu');
        closeProjectModal();
        await fetchItems();
        // Yeni proje oluşturduktan sonra direkt görsel yöneticisine yönlendir
        setImageManagerProject(res.data);
        return;
      }
      closeProjectModal();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kaydetme sırasında hata oluştu');
    } finally {
      setSavingProject(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu projeyi ve içindeki tüm görselleri silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/before-after/${id}`);
      toast.success('Proje silindi');
      fetchItems();
    } catch (err) {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  const onImagesChanged = (updatedProject) => {
    setItems((prev) => prev.map((p) => (p.id === updatedProject.id ? updatedProject : p)));
    setImageManagerProject(updatedProject);
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Öncesi / Sonrası Yönetimi</h2>
          <button className="btn btn-primary" onClick={() => openProjectModal()}>
            <FaPlus /> Yeni Proje
          </button>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Kapak</th>
              <th>Başlık</th>
              <th>Kategori</th>
              <th>Görsel Sayısı</th>
              <th>Sıra</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  Henüz proje yok
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const cover = item.images?.[0]?.after_image;
                return (
                  <tr key={item.id}>
                    <td>
                      {cover ? (
                        <img
                          src={getImageUrl(cover)}
                          alt="Kapak"
                          style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }}
                        />
                      ) : (
                        <span style={{ color: '#999', fontSize: 12 }}>Görsel yok</span>
                      )}
                    </td>
                    <td>{item.title}</td>
                    <td>{item.category || '-'}</td>
                    <td>{item.images?.length || 0}</td>
                    <td>{item.sort_order}</td>
                    <td>
                      <span className={`badge ${item.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {item.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setImageManagerProject(item)}
                          title="Görselleri Yönet"
                        >
                          <FaImages />
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => openProjectModal(item)}
                          title="Düzenle"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(item.id)}
                          title="Sil"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showProjectModal && (
        <div className="admin-modal-overlay" onClick={closeProjectModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingProject ? 'Projeyi Düzenle' : 'Yeni Proje'}</h2>
              <button className="admin-modal-close" onClick={closeProjectModal}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleProjectSubmit} className="admin-form">
              <div className="form-group">
                <label>Başlık *</label>
                <input
                  type="text"
                  className="form-control"
                  value={projectForm.title}
                  onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Kategori</label>
                <input
                  type="text"
                  className="form-control"
                  value={projectForm.category}
                  onChange={(e) => setProjectForm({ ...projectForm, category: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Sıralama</label>
                <input
                  type="number"
                  className="form-control"
                  value={projectForm.sort_order}
                  onChange={(e) => setProjectForm({ ...projectForm, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={projectForm.is_active}
                    onChange={(e) => setProjectForm({ ...projectForm, is_active: e.target.checked })}
                  />
                  Aktif
                </label>
              </div>
              {!editingProject && (
                <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
                  Proje oluşturulduktan sonra görsellerini yükleyebileceksin.
                </p>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeProjectModal}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={savingProject}>
                  {savingProject ? 'Kaydediliyor...' : editingProject ? 'Kaydet' : 'Oluştur ve Görsel Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {imageManagerProject && (
        <ImageManagerModal
          project={imageManagerProject}
          onClose={() => {
            setImageManagerProject(null);
            fetchItems();
          }}
          onChange={onImagesChanged}
        />
      )}
    </div>
  );
}

function ImageManagerModal({ project, onClose, onChange }) {
  const [images, setImages] = useState(project.images || []);
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setImages(project.images || []);
  }, [project.images, project.id]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (type === 'before') {
      setBeforeFile(file);
      setBeforePreview(url);
    } else {
      setAfterFile(file);
      setAfterPreview(url);
    }
  };

  const resetUploadForm = () => {
    setBeforeFile(null);
    setAfterFile(null);
    setBeforePreview(null);
    setAfterPreview(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!beforeFile || !afterFile) {
      toast.error('Öncesi ve sonrası görselleri seçin');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('before_image', beforeFile);
      fd.append('after_image', afterFile);
      const res = await api.post(`/before-after/${project.id}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newImages = [...images, res.data];
      setImages(newImages);
      onChange({ ...project, images: newImages });
      toast.success('Görsel çifti eklendi');
      resetUploadForm();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Yükleme sırasında hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePair = async (imageId) => {
    if (!window.confirm('Bu görsel çiftini silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/before-after/${project.id}/images/${imageId}`);
      const newImages = images.filter((img) => img.id !== imageId);
      setImages(newImages);
      onChange({ ...project, images: newImages });
      toast.success('Görsel çifti silindi');
    } catch (err) {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= images.length) return;
    const newImages = [...images];
    [newImages[index], newImages[target]] = [newImages[target], newImages[index]];
    setImages(newImages);
    try {
      await api.put(`/before-after/${project.id}/images/reorder`, {
        ids: newImages.map((img) => img.id),
      });
      onChange({ ...project, images: newImages });
    } catch (err) {
      toast.error('Sıralama güncellenemedi');
      setImages(images);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
        <div className="admin-modal-header">
          <h2>{project.title} — Görseller</h2>
          <button className="admin-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleUpload} className="admin-form" style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Yeni Görsel Çifti Ekle</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>Öncesi *</label>
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'before')} />
              {beforePreview && (
                <img src={beforePreview} alt="Öncesi önizleme" style={{ width: '100%', maxHeight: 150, objectFit: 'cover', marginTop: 8, borderRadius: 4 }} />
              )}
            </div>
            <div className="form-group">
              <label>Sonrası *</label>
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'after')} />
              {afterPreview && (
                <img src={afterPreview} alt="Sonrası önizleme" style={{ width: '100%', maxHeight: 150, objectFit: 'cover', marginTop: 8, borderRadius: 4 }} />
              )}
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Yükleniyor...' : 'Çifti Ekle'}
            </button>
          </div>
        </form>

        <h3 style={{ marginTop: 0 }}>Mevcut Görseller ({images.length})</h3>
        {images.length === 0 ? (
          <p style={{ color: '#888' }}>Henüz görsel eklenmemiş.</p>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {images.map((img, idx) => (
              <div
                key={img.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: '0.75rem',
                  alignItems: 'center',
                  padding: '0.75rem',
                  border: '1px solid #eee',
                  borderRadius: 6,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Öncesi</div>
                  <img src={getImageUrl(img.before_image)} alt="Öncesi" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4 }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Sonrası</div>
                  <img src={getImageUrl(img.after_image)} alt="Sonrası" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => move(idx, -1)} disabled={idx === 0} title="Yukarı">↑</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => move(idx, 1)} disabled={idx === images.length - 1} title="Aşağı">↓</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeletePair(img.id)} title="Sil">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
